import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { useSession } from '../providers/AuthProvider';

/**
 * Demo mode-detektion.
 *
 * En bruger er i demo-tilstand når profilens afdeling hører til den kommune
 * som `system_settings.demo_kommune_id` (is_public = true) peger på.
 * Isolationen håndhæves af eksisterende RLS-policies — dette hook er kun
 * UI-signal (badge + spærring af destruktive handlinger).
 */

// Modul-cache: samme svar for hele sessionen, undgår refetch per komponent.
let cached: { userId: string; isDemo: boolean } | null = null;

export function useDemoMode(): { isDemo: boolean; loading: boolean } {
  const { user } = useSession();
  const [state, setState] = useState<{ isDemo: boolean; loading: boolean }>(() =>
    cached && cached.userId === user?.id
      ? { isDemo: cached.isDemo, loading: false }
      : { isDemo: false, loading: !!user }
  );

  useEffect(() => {
    let cancelled = false;

    if (!user?.id) {
      setState({ isDemo: false, loading: false });
      return;
    }
    if (cached && cached.userId === user.id) {
      setState({ isDemo: cached.isDemo, loading: false });
      return;
    }

    (async () => {
      try {
        const [{ data: setting }, { data: profile }] = await Promise.all([
          supabase
            .from('system_settings')
            .select('value')
            .eq('key', 'demo_kommune_id')
            .maybeSingle(),
          supabase
            .from('profiles')
            .select('afdeling_id')
            .eq('id', user.id)
            .maybeSingle(),
        ]);

        const demoKommuneId =
          typeof setting?.value === 'string'
            ? setting.value.replace(/^"|"$/g, '')
            : null;

        let isDemo = false;
        if (demoKommuneId && profile?.afdeling_id) {
          const { data: dept } = await supabase
            .from('departments')
            .select('kommune_id')
            .eq('id', profile.afdeling_id)
            .maybeSingle();
          isDemo = dept?.kommune_id === demoKommuneId;
        }

        cached = { userId: user.id, isDemo };
        if (!cancelled) setState({ isDemo, loading: false });
      } catch {
        if (!cancelled) setState({ isDemo: false, loading: false });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  return state;
}

/**
 * Spærring af destruktive handlinger i demo-tilstand.
 * Kald før sletninger o.l.: `if (blockIfDemo()) return;`
 * Returnerer true (og viser besked) hvis handlingen skal stoppes.
 */
export function blockIfDemo(): boolean {
  if (!cached?.isDemo) return false;
  window.alert('Demo-tilstand: sletning er slået fra. Data nulstilles automatisk.');
  return true;
}
